import {
  ArticleIcon,
  CalendarBlankIcon,
  ChartBarIcon,
  ChartDonutIcon,
  ChartLineIcon,
  ChartLineUpIcon,
  ChartPolarIcon,
  CubeIcon,
  type Icon,
  ImageIcon,
  ListBulletsIcon,
  ListNumbersIcon,
  RowsIcon,
  TableIcon,
  TextAlignLeftIcon,
} from "@phosphor-icons/react";
import { explorerCatalog } from "@/lib/chat/catalog";
import { templateStudioCatalog } from "@/lib/template-studio/catalog";

/** User-facing label for a json-render component type (registry / catalog key). */
export const formatTemplateStudioComponentLabel = (
  componentType: string,
): string => {
  const words = componentType.replace(/([a-z0-9])([A-Z])/g, "$1 $2").split(" ");
  return words
    .map((word, index) =>
      index === 0
        ? `${word.charAt(0).toUpperCase()}${word.slice(1)}`
        : word.toLowerCase(),
    )
    .join(" ");
};

const structureDescriptionByComponentType = new Map<string, string>(
  [explorerCatalog, templateStudioCatalog].flatMap((catalog) =>
    Object.entries(catalog.data.components).flatMap(
      ([componentType, definition]) =>
        "structureDescription" in definition
          ? [[componentType, definition.structureDescription]]
          : [],
    ),
  ),
);

/** Short user-facing description for a structure row; falls back to the component label. */
export const getTemplateStudioComponentStructureDescription = (
  componentType: string,
): string =>
  structureDescriptionByComponentType.get(componentType) ??
  `${formatTemplateStudioComponentLabel(componentType)}.`;

const STRUCTURE_ICON_BY_COMPONENT_TYPE: Partial<Record<string, Icon>> = {
  AreaChart: ChartLineIcon,
  BarChart: ChartBarIcon,
  ComposedChart: ChartLineUpIcon,
  DataList: ListBulletsIcon,
  HorizontalBar: ChartBarIcon,
  Image: ImageIcon,
  LineChart: ChartLineIcon,
  Markdown: TextAlignLeftIcon,
  Metric: ChartLineUpIcon,
  MetricGroup: RowsIcon,
  PieChart: ChartDonutIcon,
  RadarChart: ChartPolarIcon,
  RadialBarChart: ChartDonutIcon,
  ReportDate: CalendarBlankIcon,
  ReportFooter: TableIcon,
  ReportHeader: ImageIcon,
  ReportHighlight: ArticleIcon,
  ReportSection: ArticleIcon,
  ReportTableOfContents: ListNumbersIcon,
  StackedBarChart: ChartBarIcon,
  Table: TableIcon,
};

/** Icon for a Template Studio registry component type in structure lists. */
export const resolveTemplateStudioStructureIcon = (
  componentType: string,
): Icon => STRUCTURE_ICON_BY_COMPONENT_TYPE[componentType] ?? CubeIcon;
